import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { BOOTCAMP_NOTE, TEST_CTA } from '@/config/membershipCatalog';
import QualificationFormModal from '@/components/QualificationForm';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  Target,
  ChevronDown,
  Zap,
  Trophy,
  Flame,
  Star,
  Sparkles,
  Award,
} from 'lucide-react';

// Equipo fundador (sección "Quién lo imparte"). Fotos provisionales recortadas de la pieza de equipo.
const FOUNDERS = [
  { name: 'Antonio Gimeno', role: 'CCO y Director Comercial', focus: 'Estrategia comercial, marketing y desarrollo de la red de brokers.', photo: '/team/antonio-gimeno.jpg' },
  { name: 'Daniel Serrano', role: 'COO', focus: 'Alianzas estratégicas, comunicación y construcción de comunidades.', photo: '/team/daniel-serrano.jpg' },
  { name: 'Luna Trejos', role: 'Directora de Formación y Comunidad', focus: 'Academia, membresías y activación de la comunidad.', photo: '/team/luna-trejos.jpg' },
];

export default function BootcampTokenizacion() {
  // La inscripción no es pública: el bootcamp va incluido en Premium y la plaza se reserva desde el
  // panel de miembro. Regla de Samuel (10/10/2026): sin precios y CTA único al test de idoneidad;
  // tras el test, el lead agenda su llamada.
  const [testOpen, setTestOpen] = useState(false);
  const [testSource, setTestSource] = useState('bootcamp');
  const openTest = (src = 'bootcamp') => { setTestSource(src); setTestOpen(true); };

  const scrollToProgram = () => {
    document.getElementById('programa')?.scrollIntoView({ behavior: 'smooth' });
  };

  const days = [
    {
      day: 'Día 1',
      title: 'Cambio de paradigma',
      desc: 'Entiendes cómo la digitalización y la tokenización están transformando el sector inmobiliario y aprendes a explicar el modelo del club con claridad en 90 segundos.'
    },
    {
      day: 'Día 2',
      title: 'Marco jurídico y técnico',
      desc: 'Trabajas los marcos jurídicos y técnicos de la tokenización inmobiliaria y practicas cómo comunicarlos con casos reales del sector y feedback del equipo.'
    },
    {
      day: 'Día 3',
      title: 'El día que más recuerdan',
      desc: 'El día más intenso: simulaciones, análisis de casos reales y práctica en grupo con feedback directo del equipo fundador. Termina con un encuentro de networking de la cohorte que los anteriores participantes describen como el momento que lo cambia todo.'
    },
    {
      day: 'Día 4',
      title: 'Certificación y plan de 30 días',
      desc: 'Consolidas lo aprendido, obtienes tu certificación privada FHC como Broker Digital en Tokenización Inmobiliaria y sales con un plan de acción semana a semana para el primer mes.'
    },
  ];

  return (
    <div className="min-h-screen bg-white pb-20 md:pb-0">

      {/* Hero Section */}
      <section
        className="relative min-h-screen flex items-center justify-center"
        data-testid="hero-section"
      >
        {/* Foto real (resolución media): siempre bajo degradado azul noche */}
        <img src="/media/amaroo-interior.jpg" alt="" fetchPriority="high" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#060d1b]/85 via-[#0a1628]/75 to-[#060d1b]/95" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-20">
          <div className="inline-flex items-center gap-2 border border-[#D4AF37]/50 bg-[#D4AF37]/10 backdrop-blur-sm px-5 py-2.5 mb-8 animate-fade-in-up">
            <Flame className="w-4 h-4 text-[#D4AF37]" />
            <span className="font-body text-xs uppercase tracking-widest text-[#D4AF37]">
              Bootcamp
            </span>
          </div>

          <h1 className="font-heading text-[2.1rem] sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 animate-fade-in-up delay-100" data-testid="hero-headline">
            4 días de inmersión práctica.<br />
            <span className="italic text-[#D4AF37]">Sales certificado y con tu plan de acción de 30 días.</span>
          </h1>

          <p className="font-body text-base sm:text-lg md:text-xl text-white/80 max-w-3xl mx-auto mb-4 leading-relaxed animate-fade-in-up delay-200">
            El Bootcamp donde obtienes la <strong className="text-white">certificación privada FHC como Broker Digital en Tokenización Inmobiliaria.</strong>
          </p>
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 mb-4 animate-fade-in-up delay-200">
            <span className="font-body text-sm text-[#D4AF37]">Incluido en la membresía Premium. Plazas limitadas por edición.</span>
          </div>
          <p className="font-body text-base text-white/60 max-w-2xl mx-auto mb-10 animate-fade-in-up delay-200">
            No escuchas cómo se hace. Lo practicas.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12 animate-fade-in-up delay-300">
            <Button
              onClick={() => openTest('bootcamp-hero')}
              className="h-14 px-8 bg-[#D4AF37] text-[#0F172A] hover:bg-[#C5A028] font-bold tracking-wide rounded-none inline-flex items-center justify-center gap-2 animate-pulse-gold"
              data-testid="hero-cta-primary"
            >
              {TEST_CTA}
              <ArrowRight className="w-5 h-5" />
            </Button>
            <Button
              onClick={scrollToProgram}
              variant="outline"
              className="h-14 px-8 border-white/50 text-white hover:bg-white/10 font-medium tracking-wide rounded-none backdrop-blur-sm"
              data-testid="hero-cta-secondary"
            >
              Ver programa completo
            </Button>
          </div>

          <div className="flex flex-wrap justify-center gap-2 sm:gap-4 animate-fade-in-up delay-400">
            {['4 días intensivos', 'Práctica con casos reales', 'Certificación privada FHC', 'Plan de acción de 30 días'].map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2">
                <CheckCircle2 className="w-4 h-4 text-[#D4AF37]" />
                <span className="font-body text-sm text-white">{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce hidden sm:block">
          <ChevronDown className="w-8 h-8 text-white/50" />
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="problem-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-body text-sm uppercase tracking-widest text-[#D4AF37] mb-4 block">
                El problema
              </span>
              <h2 className="font-heading text-3xl md:text-4xl text-[#0F172A] mb-6 leading-tight">
                Se habla mucho de tokenización inmobiliaria. Y casi nadie sabe <span className="italic text-[#D4AF37]">explicarla.</span>
              </h2>
              <p className="font-body text-base text-slate-600 mb-4 leading-relaxed">
                No porque el concepto sea difícil. Sino porque la mayoría de las formaciones se quedan en la teoría y nunca ponen a prueba lo aprendido.
              </p>
              <p className="font-body text-base text-slate-600 mb-4 leading-relaxed">
                Conocen el concepto. Entienden la tecnología. Pero cuando llega el momento de explicárselo con claridad a un cliente o a un colega, algo falla.
              </p>
              <div className="p-6 bg-[#0F172A] mt-6">
                <p className="font-heading text-xl text-white">
                  La formación tradicional <span className="italic text-white/60">enseña.</span>
                </p>
                <p className="font-heading text-xl text-[#D4AF37] mt-2">
                  Este bootcamp <span className="italic">entrena.</span>
                </p>
                <p className="font-body text-sm text-white/50 mt-3">
                  Y hay una diferencia enorme entre las dos cosas.
                </p>
              </div>
            </div>

            <div className="relative h-[320px] md:h-[450px] overflow-hidden">
              <img
                src="/media/amaroo-vista.jpg"
                alt="Villa de la Costa Blanca que ha acogido ediciones del bootcamp"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/60 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* What Happens in 4 Days */}
      <section className="py-24 md:py-32 bg-[#F8FAFC]" data-testid="what-happens-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-body text-sm uppercase tracking-widest text-[#D4AF37] mb-4 block">
              Lo que ocurre en 4 días
            </span>
            <h2 className="font-heading text-3xl md:text-4xl text-[#0F172A] mb-4">
              No vas a escuchar presentaciones sobre cómo se hace. <span className="italic text-[#D4AF37]">Vas a practicarlo.</span>
            </h2>
            <div className="w-16 h-px bg-[#D4AF37] mx-auto" />
          </div>

          <div className="max-w-3xl mx-auto">
            <p className="font-body text-lg text-slate-600 leading-relaxed mb-6">
              Desde el primer día entiendes el cambio que vive el sector y aprendes a explicarlo con criterio. El segundo día trabajas el marco jurídico y técnico y lo pones en práctica con casos reales del sector y el feedback del equipo.
            </p>
            <p className="font-body text-lg text-slate-600 leading-relaxed mb-6">
              El tercero es el día que todos recuerdan — simulaciones, práctica en grupo y un encuentro de networking de la cohorte que los participantes anteriores describen como <strong className="text-[#0F172A]">el momento que lo cambia todo.</strong>
            </p>
            <p className="font-body text-lg text-slate-600 leading-relaxed mb-8">
              El cuarto día consolidas, obtienes tu certificación privada FHC y sales con un plan de acción para los próximos 30 días.
            </p>
            <div className="bg-[#0F172A] p-6 text-center">
              <p className="font-heading text-2xl text-white">Cuatro días. <span className="text-[#D4AF37]">Un plan de acción claro.</span></p>
            </div>
          </div>
        </div>
      </section>

      {/* Program Section */}
      <section id="programa" className="py-24 md:py-32 relative bg-[#0F172A]" data-testid="program-section">

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <span className="font-body text-sm uppercase tracking-widest text-[#D4AF37] mb-4 block">
              El programa
            </span>
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-4">
              4 días de transformación
            </h2>
            <div className="w-16 h-px bg-[#D4AF37] mx-auto" />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {days.map((day, idx) => (
              <div
                key={idx}
                className="bg-white/5 backdrop-blur-sm border border-white/10 p-8 hover:bg-white/10 transition-colors"
                data-testid={`day-${idx + 1}`}
              >
                <div className="text-[#D4AF37] font-body text-xs uppercase tracking-widest mb-3">
                  {day.day}
                </div>
                <h3 className="font-heading text-xl text-white mb-4">{day.title}</h3>
                <p className="font-body text-sm text-white/70 leading-relaxed">{day.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-3 bg-[#D4AF37]/20 border border-[#D4AF37]/50 px-4 sm:px-6 py-4 text-left">
              <Trophy className="w-6 h-6 text-[#D4AF37] flex-shrink-0" />
              <p className="font-body text-white">
                <strong className="text-[#D4AF37]">Objetivo concreto:</strong> salir con la certificación privada FHC, criterio propio y tu plan de acción de 30 días.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="location-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <img
                src="/media/amaroo-aerea.jpg"
                alt="Vista aérea de Villa Amaroo, en la Costa Blanca"
                loading="lazy"
                className="w-full h-44 sm:h-56 object-cover"
              />
              <img
                src="/media/amaroo-piscina.jpg"
                alt="Zona exterior de Villa Amaroo"
                loading="lazy"
                className="w-full h-44 sm:h-56 object-cover"
              />
              <img
                src="/media/amaroo-sede.jpg"
                alt="Villa Amaroo, donde se han celebrado ediciones anteriores del bootcamp"
                loading="lazy"
                className="col-span-2 w-full h-48 sm:h-56 object-cover"
              />
            </div>

            <div>
              <span className="font-body text-sm uppercase tracking-widest text-[#D4AF37] mb-4 block">
                Dónde se celebra
              </span>
              <h2 className="font-heading text-3xl md:text-4xl text-[#0F172A] mb-2 leading-tight">
                No entrenas en una sala de hotel.
              </h2>
              <h3 className="font-heading text-2xl text-[#D4AF37] italic mb-6">
                Entrenas en un entorno privado, cara a cara con el equipo.
              </h3>
              <p className="font-body text-lg text-[#0F172A] font-semibold mb-2">
                Encuentros presenciales en la Costa Blanca.
              </p>
              <p className="font-body text-base text-slate-600 mb-4 leading-relaxed">
                Ediciones anteriores del bootcamp se han celebrado en Villa Amaroo, cerca de Busot (Alicante). El lugar de cada edición se confirma al reservar tu plaza.
              </p>
              <p className="font-body text-base text-slate-600 mb-6 leading-relaxed">
                Durante cuatro días entrenas en un entorno privado, analizas casos reales y practicas con el equipo. Cuando tengas que explicarle a alguien cómo la tecnología está cambiando el sector inmobiliario, no lo habrás leído en un PDF. Lo habrás practicado.
              </p>
              <p className="font-body text-sm text-[#0F172A] italic mb-6">
                Eso cambia la forma en que lo explicas.
              </p>
              <div className="space-y-3 mb-8">
                {[
                  '4 días de inmersión práctica',
                  'Networking con el equipo fundador y otros miembros',
                  'Entorno privado sin distracciones',
                  'Casos reales del sector inmobiliario',
                ].map((item, idx) => (
                  <p key={idx} className="font-body text-slate-700 flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    {item}
                  </p>
                ))}
              </div>
              <p className="font-body text-xs text-slate-500 mb-6">{BOOTCAMP_NOTE}</p>
              <Button
                onClick={() => openTest('bootcamp-ubicacion')}
                className="h-12 px-6 bg-[#0F172A] text-white hover:bg-[#1E293B] font-semibold rounded-none inline-flex items-center gap-2"
                data-testid="location-cta"
              >
                {TEST_CTA}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 md:py-32 bg-[#F8FAFC]" data-testid="testimonials-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-body text-sm uppercase tracking-widest text-[#D4AF37] mb-4 block">
              Testimonios
            </span>
            <h2 className="font-heading text-3xl md:text-4xl text-[#0F172A] mb-4">
              Lo que dicen los miembros de Freedom Homes Club
            </h2>
            <div className="w-16 h-px bg-[#D4AF37] mx-auto" />
          </div>

          {(() => {
            const testimonials = [
              {
                title: 'Ser socio de FHC es entrar en algo profesional, seguro y con mucho futuro',
                quote: 'Lo que más me sorprendió fue la profesionalidad. Hay una estructura, un respaldo legal y personas que conocen el tema al detalle. La tokenización inmobiliaria ya está aquí, y quienes entiendan esto a tiempo van a estar mejor posicionados.',
                name: 'Manoli Espinosa',
                role: 'Miembro FHC',
              },
            ];
            const TestimonialCard = ({ t, idx }) => (
              <div className="bg-white p-8 border border-slate-100 hover:border-[#D4AF37]/30 transition-colors flex flex-col" data-testid={`testimonial-${idx}`}>
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-[#D4AF37] fill-[#D4AF37]" />)}
                </div>
                <p className="font-heading text-sm text-[#0F172A] mb-3 leading-snug">"{t.title}"</p>
                <p className="font-body text-slate-500 text-sm leading-relaxed mb-6 italic flex-1">{t.quote}</p>
                <div className="border-t border-slate-100 pt-4">
                  <p className="font-heading text-sm text-[#0F172A]">{t.name}</p>
                  <p className="font-body text-xs text-slate-400">{t.role}</p>
                </div>
              </div>
            );
            // Con un solo testimonio, tarjeta centrada en lugar de una rejilla de tres columnas medio vacía
            if (testimonials.length === 1) {
              return <div className="max-w-2xl mx-auto"><TestimonialCard t={testimonials[0]} idx={0} /></div>;
            }
            return (
              <div className="space-y-6">
                <div className="grid md:grid-cols-3 gap-6">
                  {testimonials.slice(0, 3).map((t, idx) => <TestimonialCard key={idx} t={t} idx={idx} />)}
                </div>
                <div className="grid md:grid-cols-2 gap-6 md:max-w-[calc(66.666%+12px)] md:mx-auto">
                  {testimonials.slice(3).map((t, idx) => <TestimonialCard key={idx + 3} t={t} idx={idx + 3} />)}
                </div>
              </div>
            );
          })()}
        </div>
      </section>

      {/* Who Leads Section */}
      <section className="py-24 md:py-32 bg-[#0F172A]" data-testid="who-leads-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="font-body text-sm uppercase tracking-widest text-[#D4AF37] mb-4 block">
            Quién lo imparte
          </span>
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
            El equipo fundador de Freedom Homes Club
          </h2>
          <p className="font-body text-lg text-white/70 max-w-3xl mx-auto leading-relaxed mb-12">
            El bootcamp lo imparte el equipo fundador del club: profesionales que enseñan desde la práctica real del sector inmobiliario y de la tecnología blockchain, no desde la teoría.
          </p>
          <div className="mx-auto mb-12 flex max-w-4xl flex-wrap justify-center gap-6 text-left" data-testid="founders-grid">
            {FOUNDERS.map((f) => (
              <figure key={f.name} className="group w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)]">
                <div className="aspect-square overflow-hidden rounded-2xl border border-[#D4AF37]/25 bg-[#0a1628]">
                  <img
                    src={f.photo}
                    alt={`${f.name}, ${f.role}`}
                    loading="lazy"
                    width="420"
                    height="420"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                </div>
                <figcaption className="mt-4">
                  <p className="font-heading text-lg text-white leading-tight">{f.name}</p>
                  <p className="font-body text-xs uppercase tracking-widest text-[#D4AF37] mt-1">{f.role}</p>
                  <p className="font-body text-sm text-white/60 mt-2 leading-relaxed">{f.focus}</p>
                </figcaption>
              </figure>
            ))}
          </div>
          <p className="font-body text-base text-white/60 max-w-2xl mx-auto leading-relaxed mb-8">
            Con invitados en cada edición: profesionales del sector inmobiliario, legal y tecnológico seleccionados según el perfil del grupo.
          </p>
          <div className="inline-flex items-center gap-3 bg-[#D4AF37]/15 border border-[#D4AF37]/40 px-4 sm:px-6 py-4 text-left">
            <Sparkles className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
            <p className="font-body text-white/80 text-sm">
              Cada cohorte es distinta. El método es el mismo. Lo que ocurre en la sala depende de quién está en ella.
            </p>
          </div>
        </div>
      </section>

      {/* Profile Section */}
      <section className="py-24 md:py-32 bg-white" data-testid="profile-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-body text-sm uppercase tracking-widest text-[#D4AF37] mb-4 block">
              Para quién es
            </span>
            <h2 className="font-heading text-3xl md:text-4xl text-[#0F172A] mb-4">
              Estás en el lugar correcto si:
            </h2>
            <div className="w-16 h-px bg-[#D4AF37] mx-auto" />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              { icon: Zap, title: 'Vendedores y emprendedores', desc: 'Que quieren entender a fondo el sector inmobiliario digital — con un método práctico y una certificación privada que acredita su formación.' },
              { icon: Target, title: 'Agentes inmobiliarios y brokers', desc: 'Que ven cómo el mercado evoluciona y quieren ser de los primeros en saber explicar lo que la mayoría todavía no entiende.' },
              { icon: Award, title: 'Miembros Premium FHC', desc: 'Que ya tienen la membresía Premium de Freedom Homes Club y quieren completar su formación presencial y obtener la certificación privada FHC.' },
            ].map((item, idx) => (
              <div key={idx} className="text-center p-6 border border-slate-100 hover:border-[#D4AF37]/50 transition-colors">
                <div className="w-16 h-16 mx-auto bg-[#F8FAFC] flex items-center justify-center mb-4">
                  <item.icon className="w-8 h-8 text-[#0F172A]" />
                </div>
                <h3 className="font-heading text-lg text-[#0F172A] mb-2">{item.title}</h3>
                <p className="font-body text-sm text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center p-8 bg-[#0F172A]">
            <p className="font-body text-white/80">
              <strong className="text-[#D4AF37]">No es para personas que buscan inspiración.</strong> Es para personas que quieren practicar y salir con un plan de acción concreto.
              <span className="block mt-2 text-white/60">Plazas limitadas. Selección por perfil.</span>
            </p>
          </div>
        </div>
      </section>

      {/* Inscripción: solo desde el panel de miembro */}
      <section id="inscripcion" className="py-16 bg-[#0F172A] border-t border-white/[0.06]" data-testid="bootcamp-members-only">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <span className="font-body text-sm uppercase tracking-widest text-[#D4AF37] mb-3 block">Plazas</span>
          <h2 className="font-heading text-2xl md:text-3xl text-white mb-4">El Bootcamp está incluido en la membresía Premium</h2>
          <p className="font-body text-white/70 mb-3">El primer paso es el test de idoneidad. Después eliges día y hora para hablar con el equipo: te contamos las próximas ediciones y si la membresía Premium encaja contigo. Si ya eres miembro, las fechas y la reserva están en tu área privada.</p>
          <p className="font-body text-sm text-white/50 mb-8">{BOOTCAMP_NOTE}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button type="button" onClick={() => openTest('bootcamp-plazas')} className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0F172A] font-bold px-6 h-12" data-testid="plazas-cta">{TEST_CTA} <ArrowRight className="w-4 h-4" /></button>
            <Link to="/dashboard/eventos" className="inline-flex items-center justify-center border border-white/25 text-white px-6 h-12">Ya soy miembro: reservar mi plaza</Link>
          </div>
        </div>
      </section>


      {/* Sticky Mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white/95 backdrop-blur-sm border-t border-slate-200 shadow-lg md:hidden">
        <Button
          onClick={() => openTest('bootcamp-sticky')}
          className="w-full h-12 bg-[#D4AF37] text-[#0F172A] hover:bg-[#C5A028] font-bold rounded-none inline-flex items-center justify-center gap-2"
          data-testid="sticky-cta"
        >
          {TEST_CTA} <ArrowRight className="w-4 h-4" />
        </Button>
      </div>

      <QualificationFormModal isOpen={testOpen} onClose={() => setTestOpen(false)} source={testSource} />
    </div>
  );
}
